import WebApp from '@twa-dev/sdk';

/**
 * Extended WebApp type for Bot API 8.0+ features not yet in @twa-dev/sdk v7.x.
 * We access window.Telegram.WebApp directly because the SDK wrapper (v7.x)
 * may not proxy methods it doesn't know about.
 */
interface SafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface NativeTelegramWebApp {
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
  isFullscreen?: boolean;
  safeAreaInset?: SafeAreaInset;
  contentSafeAreaInset?: SafeAreaInset;
  isVersionAtLeast: (version: string) => boolean;
  onEvent: (eventType: string, handler: (...args: unknown[]) => void) => void;
  offEvent: (eventType: string, handler: (...args: unknown[]) => void) => void;
  setHeaderColor: (color: string) => void;
  expand: () => void;
  ready: () => void;
}

interface TelegramWebView {
  postEvent: (eventType: string, callback?: (() => void) | false, eventData?: unknown) => void;
  onEvent: (eventType: string, callback: (...args: unknown[]) => void) => void;
  offEvent: (eventType: string, callback: (...args: unknown[]) => void) => void;
}

interface TelegramGlobal {
  WebApp?: NativeTelegramWebApp;
  WebView?: TelegramWebView;
}

/**
 * Get the native Telegram WebApp object directly from window.
 * This bypasses the @twa-dev/sdk wrapper which may not expose Bot API 8.0+ methods.
 */
function getNativeWebApp(): NativeTelegramWebApp | null {
  const tgWindow = window as unknown as Window & { Telegram?: TelegramGlobal };
  return tgWindow.Telegram?.WebApp ?? null;
}

/**
 * Get the low-level Telegram WebView bridge.
 * This allows sending events directly to the native Telegram client,
 * bypassing WebApp methods that may not exist in older injected scripts.
 */
function getTelegramWebView(): TelegramWebView | null {
  const tgWindow = window as unknown as Window & { Telegram?: TelegramGlobal };
  return tgWindow.Telegram?.WebView ?? null;
}

/**
 * Stored safe area values — updated via bridge events or native properties.
 */
let currentSafeArea: SafeAreaInset = { top: 0, bottom: 0, left: 0, right: 0 };
let currentContentSafeArea: SafeAreaInset = { top: 0, bottom: 0, left: 0, right: 0 };

/**
 * Whether we have received at least one set of non-zero safe area values from
 * the native SDK in this page session.  While this is `false` (i.e. right after
 * a page refresh, before the SDK responds), we protect persisted/fallback values
 * from being overwritten with 0s.  Once the SDK sends real values, we set this
 * to `true` and accept ALL subsequent values — including smaller ones caused by
 * orientation changes (landscape has a smaller top safe area than portrait).
 */
let hasReceivedNativeValues = false;

/**
 * Whether fullscreen mode has been requested.
 * Persisted in sessionStorage so it survives page refreshes — the SafeAreaHeader
 * can read it synchronously on the very first render (before initTelegram runs)
 * and avoid collapsing to 0px.
 *
 * We also persist the last-known safe area values so that on refresh, ALL CSS
 * that depends on --safe-area-top (e.g. #root padding-top, sticky headers)
 * gets correct values immediately, preventing the content "jump to top" effect.
 */
const FS_STORAGE_KEY = '__tg_fs';
const SA_STORAGE_KEY = '__tg_sa'; // persisted safe area values
let fullscreenRequested = sessionStorage.getItem(FS_STORAGE_KEY) === '1';
const FULLSCREEN_LISTENERS: Array<(isFullscreen: boolean) => void> = [];

/**
 * Default fallback safe area values for iOS devices with notch.
 * Used when fullscreen was requested but no persisted values exist yet.
 */
const FALLBACK_SAFE_AREA: SafeAreaInset = { top: 59, bottom: 0, left: 0, right: 0 };

/**
 * On module load: if fullscreen was previously requested, immediately apply
 * persisted (or fallback) safe area CSS vars so that #root padding-top,
 * sticky headers, and the SafeAreaHeader all have sensible values before
 * React even renders. This prevents the "jump to top" on refresh.
 */
if (fullscreenRequested) {
  try {
    const stored = sessionStorage.getItem(SA_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as { sa: SafeAreaInset; csa: SafeAreaInset };
      currentSafeArea = parsed.sa;
      currentContentSafeArea = parsed.csa;
    } else {
      // No persisted values yet — use sensible fallback
      currentSafeArea = { ...FALLBACK_SAFE_AREA };
    }
  } catch {
    currentSafeArea = { ...FALLBACK_SAFE_AREA };
  }
  // Apply to CSS vars immediately (runs at module load, before React render).
  // applySafeAreaCSSVars is a hoisted function declaration, safe to call here.
  applySafeAreaCSSVars();
}

function markFullscreenRequested() {
  if (!fullscreenRequested) {
    fullscreenRequested = true;
    try { sessionStorage.setItem(FS_STORAGE_KEY, '1'); } catch { /* quota */ }
  }
}

/**
 * Persist current safe area values to sessionStorage so they survive refresh.
 */
function persistSafeAreaValues() {
  try {
    sessionStorage.setItem(SA_STORAGE_KEY, JSON.stringify({
      sa: currentSafeArea,
      csa: currentContentSafeArea,
    }));
  } catch { /* quota */ }
}

/**
 * Check whether fullscreen was requested and is expected to be active.
 * Returns true synchronously even on first render after a refresh.
 */
export function isFullscreenExpected(): boolean {
  if (fullscreenRequested) return true;
  const native = getNativeWebApp();
  return native?.isFullscreen === true;
}

/**
 * Subscribe to fullscreen state changes. Returns unsubscribe function.
 */
export function onFullscreenChange(listener: (isFullscreen: boolean) => void): () => void {
  FULLSCREEN_LISTENERS.push(listener);
  return () => {
    const idx = FULLSCREEN_LISTENERS.indexOf(listener);
    if (idx >= 0) FULLSCREEN_LISTENERS.splice(idx, 1);
  };
}

function notifyFullscreenListeners() {
  const isFs = isFullscreenExpected();
  for (const listener of FULLSCREEN_LISTENERS) {
    listener(isFs);
  }
}

/**
 * Apply current safe area values to CSS custom properties.
 * This is a function declaration (hoisted), so it can be called at module load
 * from the early-init block above.
 */
function applySafeAreaCSSVars() {
  const root = document.documentElement;
  root.style.setProperty('--tg-safe-area-inset-top', `${currentSafeArea.top}px`);
  root.style.setProperty('--tg-safe-area-inset-bottom', `${currentSafeArea.bottom}px`);
  root.style.setProperty('--tg-safe-area-inset-left', `${currentSafeArea.left}px`);
  root.style.setProperty('--tg-safe-area-inset-right', `${currentSafeArea.right}px`);
  root.style.setProperty('--tg-content-safe-area-inset-top', `${currentContentSafeArea.top}px`);
  root.style.setProperty('--tg-content-safe-area-inset-bottom', `${currentContentSafeArea.bottom}px`);
  root.style.setProperty('--tg-content-safe-area-inset-left', `${currentContentSafeArea.left}px`);
  root.style.setProperty('--tg-content-safe-area-inset-right', `${currentContentSafeArea.right}px`);

  // Persist values so they survive page refresh
  if (fullscreenRequested) {
    persistSafeAreaValues();
  }
}

/**
 * Parse safe area data from a bridge event.
 * The bridge may pass (eventType, data) or just (data).
 */
function parseSafeAreaFromEvent(args: unknown[]): SafeAreaInset | null {
  // Try first arg, then second (in case first is eventType string)
  for (const arg of args) {
    if (arg && typeof arg === 'object' && 'top' in (arg as Record<string, unknown>)) {
      const obj = arg as Record<string, unknown>;
      if (typeof obj.top === 'number') {
        return {
          top: obj.top as number,
          bottom: (obj.bottom as number) ?? 0,
          left: (obj.left as number) ?? 0,
          right: (obj.right as number) ?? 0,
        };
      }
    }
  }
  return null;
}

/**
 * Try to read safe area values from native WebApp properties.
 * These may or may not exist depending on the version of telegram-web-app.js.
 */
function readNativeSafeAreaValues() {
  const native = getNativeWebApp();
  if (!native) return;
  if (native.safeAreaInset && typeof native.safeAreaInset.top === 'number') {
    const incoming = native.safeAreaInset;
    if (hasReceivedNativeValues) {
      // SDK already confirmed — accept any value (handles orientation changes)
      currentSafeArea = incoming;
    } else if (incoming.top > 0) {
      // First real value from SDK — unlock and accept
      hasReceivedNativeValues = true;
      currentSafeArea = incoming;
    }
    // else: still waiting for SDK, keep persisted/fallback values
  }
  if (native.contentSafeAreaInset && typeof native.contentSafeAreaInset.top === 'number') {
    const incoming = native.contentSafeAreaInset;
    if (hasReceivedNativeValues) {
      currentContentSafeArea = incoming;
    } else if (incoming.top > 0) {
      hasReceivedNativeValues = true;
      currentContentSafeArea = incoming;
    }
  }
}

/**
 * Initialize Telegram WebApp
 */
export function initTelegram() {
  // Expand to full height
  WebApp.expand();

  // Disable vertical swipes to prevent closing the app when scrolling down at the top
  WebApp.disableVerticalSwipes();

  // Access native Telegram WebApp and low-level bridge for Bot API 8.0+ features
  const native = getNativeWebApp();
  const webView = getTelegramWebView();

  // Skip fullscreen on desktop platforms where it doesn't work properly
  // and the user cannot exit fullscreen mode.
  // Cast to string because the SDK types may not include all runtime platform values
  // (e.g. 'tdesktop', 'weba', 'web' are real values not in the type definition).
  const platform = WebApp.platform as string;
  const DESKTOP_PLATFORMS = ['tdesktop', 'weba', 'web', 'webk', 'macos', 'unigram'];
  const isDesktop = DESKTOP_PLATFORMS.includes(platform);

  if (native && native.isVersionAtLeast('8.0') && webView && !isDesktop) {
    // Listen for safe area changes via the low-level bridge.
    // We use bridge events because native.onEvent('safeAreaChanged') may not
    // work if the injected telegram-web-app.js doesn't know about these events.
    webView.onEvent('safe_area_changed', ((...args: unknown[]) => {
      const data = parseSafeAreaFromEvent(args);
      if (!data) return;
      if (hasReceivedNativeValues || data.top > 0) {
        if (data.top > 0) hasReceivedNativeValues = true;
        currentSafeArea = data;
        applySafeAreaCSSVars();
      }
    }) as (...args: unknown[]) => void);

    webView.onEvent('content_safe_area_changed', ((...args: unknown[]) => {
      const data = parseSafeAreaFromEvent(args);
      if (!data) return;
      if (hasReceivedNativeValues || data.top > 0) {
        if (data.top > 0) hasReceivedNativeValues = true;
        currentContentSafeArea = data;
        applySafeAreaCSSVars();
      }
    }) as (...args: unknown[]) => void);

    webView.onEvent('fullscreen_changed', ((..._args: unknown[]) => {
      // Re-read safe area values after fullscreen state changes
      readNativeSafeAreaValues();
      applySafeAreaCSSVars();
    }) as (...args: unknown[]) => void);

    // Request fullscreen using the low-level bridge directly.
    markFullscreenRequested();
    notifyFullscreenListeners();
    webView.postEvent('web_app_request_fullscreen');

    // Set header color for proper status bar contrast in fullscreen
    try {
      native.setHeaderColor(WebApp.themeParams.bg_color ?? '#ffffff');
    } catch {
      // Ignore if not supported
    }

    // Read initial safe area values from native properties (may exist)
    readNativeSafeAreaValues();
    applySafeAreaCSSVars();

    // Also try native.onEvent as fallback
    try {
      native.onEvent('safeAreaChanged', (() => {
        readNativeSafeAreaValues();
        applySafeAreaCSSVars();
      }) as (...args: unknown[]) => void);
      native.onEvent('contentSafeAreaChanged', (() => {
        readNativeSafeAreaValues();
        applySafeAreaCSSVars();
      }) as (...args: unknown[]) => void);
    } catch {
      // Ignore if not supported
    }

    // Poll for safe area values — on refresh, the fullscreen request may take
    // a moment to be acknowledged by the native client. We use increasing
    // intervals to balance responsiveness with efficiency.
    const pollDelays = [50, 100, 200, 400, 800]; // 5 polls over ~1.5s total
    let pollIndex = 0;
    const schedulePoll = () => {
      if (pollIndex >= pollDelays.length) return;
      if (currentSafeArea.top > 0 || currentContentSafeArea.top > 0) return;
      setTimeout(() => {
        readNativeSafeAreaValues();
        applySafeAreaCSSVars();
        notifyFullscreenListeners();
        pollIndex++;
        schedulePoll();
      }, pollDelays[pollIndex]);
    };
    schedulePoll();

    // Re-request fullscreen when the page becomes visible again (e.g. after
    // switching apps or tabs). This handles the case where fullscreen is lost
    // on background/foreground transitions.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        const wv = getTelegramWebView();
        if (wv) {
          wv.postEvent('web_app_request_fullscreen');
        }
        // Re-read safe area values after a short delay
        setTimeout(() => {
          readNativeSafeAreaValues();
          applySafeAreaCSSVars();
          notifyFullscreenListeners();
        }, 150);
      }
    });
  }

  // Ready event
  WebApp.ready();
}

/**
 * Get current Telegram user
 */
export function getTelegramUser(): typeof WebApp.initDataUnsafe.user {
  return WebApp.initDataUnsafe.user;
}

/**
 * Get init data for API authentication
 */
export function getInitData() {
  return WebApp.initData;
}

/**
 * Get start parameter from deep link
 */
export function getStartParam() {
  return WebApp.initDataUnsafe.start_param;
}

/**
 * Show main button
 */
export function showMainButton(text: string, onClick: () => void) {
  WebApp.MainButton.setText(text);
  WebApp.MainButton.onClick(onClick);
  WebApp.MainButton.show();
}

/**
 * Hide main button
 */
export function hideMainButton() {
  WebApp.MainButton.hide();
}

/**
 * Set main button loading state
 */
export function setMainButtonLoading(loading: boolean) {
  if (loading) {
    WebApp.MainButton.showProgress();
  } else {
    WebApp.MainButton.hideProgress();
  }
}

/**
 * Show popup
 */
export function showPopup(
  title: string,
  message: string,
  buttons?: { id?: string; type: 'default' | 'ok' | 'close' | 'cancel' | 'destructive'; text: string }[]
): Promise<string> {
  return new Promise((resolve) => {
    WebApp.showPopup(
      {
        title,
        message,
        buttons: buttons || [{ type: 'ok', text: 'OK' }],
      },
      (buttonId) => {
        resolve(buttonId || 'ok');
      }
    );
  });
}

/**
 * Show alert
 */
export function showAlert(message: string): Promise<void> {
  return new Promise((resolve) => {
    WebApp.showAlert(message, () => resolve());
  });
}

/**
 * Show confirm
 */
export function showConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    WebApp.showConfirm(message, (confirmed) => resolve(confirmed));
  });
}

/**
 * Close the mini app
 */
export function closeMiniApp() {
  WebApp.close();
}

/**
 * Open an external link in the browser without closing the mini app
 * This is essential for Stripe checkout to work properly
 */
export function openExternalLink(url: string, tryInstantView = false) {
  WebApp.openLink(url, { try_instant_view: tryInstantView });
}

/**
 * Open a Telegram link (t.me/...) directly inside Telegram without closing the mini app.
 * Falls back to window.open if the SDK method is unavailable.
 */
export function openTelegramLink(url: string) {
  try {
    if (typeof WebApp.openTelegramLink === 'function') {
      WebApp.openTelegramLink(url);
    } else {
      window.open(url, '_blank');
    }
  } catch {
    window.open(url, '_blank');
  }
}

/**
 * Trigger haptic feedback
 */
export function hapticFeedback(
  type: 'impact' | 'notification' | 'selection',
  style?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' | 'error' | 'success' | 'warning'
) {
  if (type === 'impact') {
    WebApp.HapticFeedback.impactOccurred(style as 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' || 'medium');
  } else if (type === 'notification') {
    WebApp.HapticFeedback.notificationOccurred(style as 'error' | 'success' | 'warning' || 'success');
  } else {
    WebApp.HapticFeedback.selectionChanged();
  }
}

/**
 * Get color scheme
 */
export function getColorScheme() {
  return WebApp.colorScheme;
}

/**
 * Register callback for when the Mini App is deactivated (minimized/backgrounded)
 * Uses browser's visibilitychange event as a fallback since @twa-dev/sdk
 * might not have types for Bot API 8.0+ events yet.
 * Also listens to pagehide for additional coverage on mobile.
 */
export function onDeactivated(callback: () => void): () => void {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      callback();
    }
  };

  const handlePageHide = () => {
    callback();
  };

  // Use visibility change as primary method (works on all platforms)
  document.addEventListener('visibilitychange', handleVisibilityChange);
  // Also use pagehide for iOS/Safari which may not fire visibilitychange reliably
  window.addEventListener('pagehide', handlePageHide);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
  };
}

/**
 * Register callback for when the Mini App is activated (restored/focused)
 * Uses browser's visibilitychange event.
 */
export function onActivated(callback: () => void): () => void {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      callback();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/**
 * Check if the app is currently active
 */
export function isAppActive(): boolean {
  return document.visibilityState === 'visible';
}

export { WebApp };

